import React, { useState } from 'react'
import { useAuth } from '@/hooks/useAuth';
import { Dialog, DialogContent, DialogClose } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Link } from 'react-router-dom'
import { useAlbumNavigation } from '@/hooks/useAlbumNavigation'
import { useTranslation } from '@/hooks/useTranslation'
import type { MediaTileData } from './types'
import { optimizeCloudinaryUrl, downloadImage } from '@/lib/utils'
import { Download, EyeOff, ChevronLeft, ChevronRight, X, Pencil, Check } from 'lucide-react'
import { useTheme } from '@/contexts/ThemeProvider'
import { useSettings } from '@/hooks/useSettings'
import { useAlbumContext } from '@/contexts/AlbumProvider';
import { useAlbumInteractions } from '@/contexts/AlbumInteractionsContext';
import { toast } from '@/hooks/use-toast';
import { useMediaVoters } from '@/hooks/useMediaVoters';
import { useComments } from '@/hooks/useComments';
import { useSurfaceContrastVariables } from '@/hooks/useSurfaceContrastVariables';

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  item: MediaTileData | null
  items?: MediaTileData[]
  currentIndex?: number
  onNavigate?: (index: number) => void
  showUploader?: boolean
  canEditDescriptions?: boolean
  onUpdateDescription?: (itemId: string, description: string) => Promise<void>
  onToggleFlag: (item: MediaTileData) => void | Promise<void>
  onSingleDelete: (item: MediaTileData) => void | Promise<void>
}

export const MediaInfoModal: React.FC<Props> = ({
  open, 
  onOpenChange, 
  item, 
  items = [], 
  currentIndex = 0, 
  onNavigate, 
  showUploader = true,
  canEditDescriptions = false,
  onUpdateDescription,
  onToggleFlag, 
  onSingleDelete 
}) => {
  const { user } = useAuth();
  const { getAlbumPath } = useAlbumNavigation()
  const { albumSlug } = useAlbumContext();
  const { tokens } = useTheme()
  const { t } = useTranslation()
  const { getVotesCount, getCommentsCount } = useAlbumInteractions()
  const { getBooleanSetting } = useSettings()
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    information: true,
    uploader: true
  })
  const [expandedLikes, setExpandedLikes] = useState(false)
  const [expandedComments, setExpandedComments] = useState(false)
  const [editingDescription, setEditingDescription] = useState(false)
  const [editedDescription, setEditedDescription] = useState(item?.description || '')
  const showCommentsRow = getBooleanSetting('enable_comments', false)
  const showVotesRow = getBooleanSetting('enable_likes', false)
  const showModeration = getBooleanSetting('enable_moderation', false)
  const isDownloadEnabled = getBooleanSetting('enable_single_image_download', false);
  const preferredSurfaceText = tokens.colors.foreground;
  const preferredMutedSurfaceText = tokens.colors['muted-foreground'] || preferredSurfaceText;
  const hasNavigation = items.length > 1
  const mediaId = item?.id || null;
  const { voters, loading: loadingVoters } = useMediaVoters(mediaId, open && expandedLikes && showVotesRow);
  const { comments, loading: loadingComments } = useComments(mediaId);
  const resolvedVotesCount = item ? getVotesCount(item.id, item.votes_count ?? 0) : 0;
  const resolvedCommentsCount = item ? getCommentsCount(item.id, item.comments_count ?? 0) : 0;
  const { ref: leftNavSurfaceRef } = useSurfaceContrastVariables<HTMLDivElement>({
    preferredText: preferredSurfaceText,
    disabled: !open || !hasNavigation,
  })
  const { ref: rightNavSurfaceRef } = useSurfaceContrastVariables<HTMLDivElement>({
    preferredText: preferredSurfaceText,
    disabled: !open || !hasNavigation,
  })
  const { ref: infoHeaderSurfaceRef } = useSurfaceContrastVariables<HTMLButtonElement>({
    preferredText: preferredMutedSurfaceText,
    disabled: !open,
  })
  const { ref: uploaderHeaderSurfaceRef } = useSurfaceContrastVariables<HTMLButtonElement>({
    preferredText: preferredMutedSurfaceText,
    disabled: !open || !showUploader || !item?.uploaded_by,
  })
  const { ref: likesPanelSurfaceRef } = useSurfaceContrastVariables<HTMLDivElement>({
    preferredText: preferredSurfaceText,
    disabled: !open || !expandedLikes || !showVotesRow,
  })
  const { ref: commentsPanelSurfaceRef } = useSurfaceContrastVariables<HTMLDivElement>({
    preferredText: preferredSurfaceText,
    disabled: !open || !expandedComments || !showCommentsRow,
  })
  const { ref: uploaderRowSurfaceRef } = useSurfaceContrastVariables<HTMLAnchorElement>({
    preferredText: preferredSurfaceText,
    disabled: !open || !showUploader || !item?.uploaded_by,
  })
  const { ref: footerSurfaceRef } = useSurfaceContrastVariables<HTMLDivElement>({
    preferredText: tokens.colors['card-foreground'] || preferredSurfaceText,
    disabled: !open,
  })

  const formattedUpload = React.useMemo(() => {
    const dateStr = item?.inserted_at || item?.created_at
    if (!dateStr) return '-'
    try {
      const date = new Date(dateStr)
      return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'long',
        timeStyle: 'short',
      }).format(date)
    } catch {
      return '-'
    }
  }, [item?.inserted_at, item?.created_at])

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  const handlePrev = () => {
    if (!onNavigate) return
    if (items.length === 0) return
    const newIndex = currentIndex === 0 ? items.length - 1 : currentIndex - 1
    onNavigate(newIndex)
  }

  const handleNext = () => {
    if (!onNavigate) return
    if (items.length === 0) return
    const newIndex = currentIndex === items.length - 1 ? 0 : currentIndex + 1
    onNavigate(newIndex)
  }

  React.useEffect(() => {
    if (!open || !hasNavigation) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        handlePrev()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        handleNext()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, hasNavigation, currentIndex, items.length])

  React.useEffect(() => {
    setExpandedLikes(false)
    setExpandedComments(false)
  }, [item?.id, open])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl h-[90vh] p-0 gap-0 flex flex-col md:grid md:grid-cols-[1fr_20rem] md:h-auto md:max-h-[90vh] rounded-lg md:rounded-lg overflow-hidden">
        {/* Close button - positioned for both mobile and desktop */}
        <DialogClose className="absolute top-4 right-4 z-50 flex items-center justify-center w-8 h-8 rounded-full bg-white shadow-lg opacity-90 hover:opacity-100 ring-offset-background transition-opacity disabled:pointer-events-none">
          <X className="h-5 w-5 text-black" />
        </DialogClose>

        {item && (
          <>
            {/* Left: Image Area (Desktop) / Full Width (Mobile) */}
            <div className="relative w-full bg-black flex items-center justify-center md:rounded-l-lg overflow-hidden order-1 md:order-1 md:max-h-[90vh] md:overflow-auto p-2 md:p-4">
              {/* Item counter */}
              {hasNavigation && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 bg-black/60 text-white px-3 py-1 rounded-full text-xs font-medium">
                  {currentIndex + 1} / {items.length}
                </div>
              )}

              {/* Navigation: Desktop side buttons with extended clickable area */}
              {hasNavigation && (
                <>
                  <button
                    onClick={handlePrev}
                    className="absolute left-0 top-0 bottom-0 z-10 hidden md:flex items-center justify-center w-20 hover:bg-black/10 transition-colors"
                  >
                    <div
                      ref={leftNavSurfaceRef}
                      className="flex items-center justify-center w-10 h-10 rounded-lg bg-muted/80 hover:bg-muted text-[hsl(var(--surface-fg))]"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </div>
                  </button>
                  <button
                    onClick={handleNext}
                    className="absolute right-0 top-0 bottom-0 z-10 hidden md:flex items-center justify-center w-20 hover:bg-black/10 transition-colors"
                  >
                    <div
                      ref={rightNavSurfaceRef}
                      className="flex items-center justify-center w-10 h-10 rounded-lg bg-muted/80 hover:bg-muted text-[hsl(var(--surface-fg))]"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </div>
                  </button>
                </>
              )}

              {/* Navigation: Mobile side buttons with extended clickable area */}
              {hasNavigation && (
                <>
                  <button
                    onClick={handlePrev}
                    className="absolute left-0 top-0 bottom-0 z-10 flex md:hidden items-center justify-center w-16 hover:bg-black/10 transition-colors"
                  >
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-black/50 hover:bg-black/70 text-white">
                      <ChevronLeft className="w-5 h-5" />
                    </div>
                  </button>
                  <button
                    onClick={handleNext}
                    className="absolute right-0 top-0 bottom-0 z-10 flex md:hidden items-center justify-center w-16 hover:bg-black/10 transition-colors"
                  >
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-black/50 hover:bg-black/70 text-white">
                      <ChevronRight className="w-5 h-5" />
                    </div>
                  </button>
                </>
              )}

              <img
                src={item.cdn_display_m || item.cdn_display_s || optimizeCloudinaryUrl({ originalUrl: item.secure_url, width: 1200 })}
                alt={t('mediaAlt')}
                className="max-w-full max-h-[80vh] md:max-h-[85vh] object-contain pointer-events-none select-none relative z-0"
              />
            </div>

            {/* Right Sidebar (Desktop) / Bottom Content (Mobile) */}
            <div className="flex flex-col order-2 md:order-2 md:border-l md:border-border md:max-h-[90vh] md:overflow-y-auto md:rounded-r-lg">

              {/* Scrollable Content Area */}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 md:pt-16">
                {/* Information Section */}
                <div className="border rounded-lg overflow-hidden">
                  <button
                    ref={infoHeaderSurfaceRef}
                    onClick={() => toggleSection('information')}
                    className="w-full flex items-center justify-between px-3 py-2 bg-muted/50 hover:bg-muted transition-colors text-[hsl(var(--surface-fg))]"
                    style={{
                      paddingTop: 'calc(0.5rem + var(--heading-top-offset, 0px))',
                      paddingBottom: '0.5rem',
                    }}
                  >
                    <h3 className="text-sm font-medium leading-none text-[hsl(var(--surface-muted-fg))]">{t('informationLabel') || 'Información'}</h3>
                    <ChevronRight className={`w-4 h-4 text-[hsl(var(--surface-muted-fg))] transition-transform ${expandedSections.information ? 'rotate-90' : ''}`} />
                  </button>
                  {expandedSections.information && (
                    <div className="divide-y divide-border text-sm">
                      {canEditDescriptions ? (
                        <div className="px-3 py-2 flex flex-col gap-1">
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground text-sm font-medium">{t('descriptionLabel') || 'Descripción'}</span>
                            {canEditDescriptions && !editingDescription && (
                              <button
                                onClick={() => {
                                  setEditingDescription(true)
                                  setEditedDescription(item.description || '')
                                }}
                                className="rounded p-1.5 hover:bg-accent transition-colors flex items-center justify-center"
                                aria-label="Edit description"
                              >
                                <Pencil className="h-4 w-4 text-foreground" />
                              </button>
                            )}
                          </div>
                          {editingDescription ? (
                            <div className="flex gap-2 mt-2">
                              <textarea
                                value={editedDescription}
                                onChange={(e) => setEditedDescription(e.target.value)}
                                placeholder={t('addDescription') || 'Agrega una descripción'}
                                className="flex-1 text-sm border rounded p-2 resize-none"
                                rows={3}
                              />
                              <div className="flex flex-col gap-1">
                                <button
                                  onClick={async () => {
                                    if (onUpdateDescription && item) {
                                      try {
                                        await onUpdateDescription(item.id, editedDescription)
                                        setEditingDescription(false)
                                      } catch (e) {
                                        console.error('Error updating description:', e)
                                      }
                                    }
                                  }}
                                  className="rounded p-1 hover:bg-green-100 transition-colors"
                                  aria-label="Save description"
                                >
                                  <Check className="h-4 w-4 text-green-600" />
                                </button>
                              </div>
                            </div>
                          ) : (
                            <p className={`whitespace-pre-wrap break-words text-sm ${item.description ? 'text-foreground' : 'text-muted-foreground italic'}`}>
                              {item.description || (canEditDescriptions ? t('addDescription') || 'Agrega una descripción' : '')}
                            </p>
                          )}
                        </div>
                      ) : null}
                      <div className="px-3 py-2 flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">{t('uploadedTableHeader')}</span>
                        <span className="text-foreground">{formattedUpload}</span>
                      </div>
                      {showVotesRow && (
                        <div className="px-3 py-2 text-sm">
                          <button
                            onClick={() => setExpandedLikes((prev) => !prev)}
                            className="w-full flex items-center justify-between hover:text-foreground transition-colors"
                          >
                            <span className="text-muted-foreground">{t('votesTableHeader')}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-foreground">{resolvedVotesCount}</span>
                              <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${expandedLikes ? 'rotate-90' : ''}`} />
                            </div>
                          </button>
                          {expandedLikes && (
                            <div
                              ref={likesPanelSurfaceRef}
                              className="mt-2 max-h-[300px] overflow-y-auto rounded-md border bg-muted/20 text-[hsl(var(--surface-fg))]"
                            >
                              {loadingVoters ? (
                                <p className="px-3 py-2 text-xs text-[hsl(var(--surface-muted-fg))]">{t('loadingComments')}</p>
                              ) : voters.length === 0 ? (
                                <p className="px-3 py-2 text-xs text-[hsl(var(--surface-muted-fg))]">{t('noVotesYet')}</p>
                              ) : (
                                <ul className="divide-y divide-border">
                                  {voters.map((voter) => (
                                    <li key={voter.user_id} className="px-3 py-2 flex items-center gap-2">
                                      <Avatar className="h-6 w-6">
                                        <AvatarImage src={voter.avatar_url || undefined} />
                                        <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                                          {(voter.display_name || 'U')
                                            .split(' ')
                                            .map((n) => n[0])
                                            .join('')
                                            .toUpperCase()
                                            .slice(0, 2)}
                                        </AvatarFallback>
                                      </Avatar>
                                      <span className="text-xs text-[hsl(var(--surface-fg))] truncate">{voter.display_name || '-'}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      {showCommentsRow && (
                        <div className="px-3 py-2 text-sm">
                          <button
                            onClick={() => setExpandedComments((prev) => !prev)}
                            className="w-full flex items-center justify-between hover:text-foreground transition-colors"
                          >
                            <span className="text-muted-foreground">{t('comments')}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-foreground">{loadingComments ? resolvedCommentsCount : comments.length}</span>
                              <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${expandedComments ? 'rotate-90' : ''}`} />
                            </div>
                          </button>
                          {expandedComments && (
                            <div
                              ref={commentsPanelSurfaceRef}
                              className="mt-2 max-h-[300px] overflow-y-auto rounded-md border bg-muted/20 text-[hsl(var(--surface-fg))]"
                            >
                              {loadingComments ? (
                                <p className="px-3 py-2 text-xs text-[hsl(var(--surface-muted-fg))]">{t('loadingComments')}</p>
                              ) : comments.length === 0 ? (
                                <p className="px-3 py-2 text-xs text-[hsl(var(--surface-muted-fg))]">{t('noComments')}</p>
                              ) : (
                                <ul className="divide-y divide-border">
                                  {comments.map((comment) => (
                                    <li key={comment.id} className="px-3 py-2">
                                      <div className="flex items-center gap-2 mb-1">
                                        <Avatar className="h-6 w-6">
                                          <AvatarImage src={comment.avatar_url || undefined} />
                                          <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                                            {(comment.display_name || 'U')
                                              .split(' ')
                                              .map((n) => n[0])
                                              .join('')
                                              .toUpperCase()
                                              .slice(0, 2)}
                                          </AvatarFallback>
                                        </Avatar>
                                        <span className="text-xs font-medium text-[hsl(var(--surface-fg))] truncate">{comment.display_name || '-'}</span>
                                      </div>
                                      <p className="whitespace-pre-wrap break-words pl-8 text-xs text-[hsl(var(--surface-muted-fg))]">
                                        {comment.content}
                                      </p>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Uploader Section */}
                {item.uploaded_by && showUploader && (
                  <div className="border rounded-lg overflow-hidden">
                  <button
                    ref={uploaderHeaderSurfaceRef}
                    onClick={() => toggleSection('uploader')}
                    className="w-full flex items-center justify-between px-3 py-2 bg-muted/50 hover:bg-muted transition-colors text-[hsl(var(--surface-fg))]"
                    style={{
                      paddingTop: 'calc(0.5rem + var(--heading-top-offset, 0px))',
                      paddingBottom: '0.5rem',
                    }}
                  >
                      <h3 className="text-sm font-medium leading-none text-[hsl(var(--surface-muted-fg))]">{t('userTableHeader')}</h3>
                      <ChevronRight className={`w-4 h-4 text-[hsl(var(--surface-muted-fg))] transition-transform ${expandedSections.uploader ? 'rotate-90' : ''}`} />
                    </button>
                    {expandedSections.uploader && (
                      <Link
                        ref={uploaderRowSurfaceRef}
                        to={getAlbumPath(`/usuario?userId=${item.uploaded_by}`)}
                        className="flex items-center gap-2 border-t border-border p-2 text-[hsl(var(--surface-fg))] transition-colors hover:bg-muted/50"
                      >
                        <Avatar className="h-7 w-7">
                          <AvatarImage src={item.uploader_avatar || undefined} />
                          <AvatarFallback className="text-xs bg-primary/10 text-primary text-[10px]">
                            {(item.uploader_name || 'U').split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium text-[hsl(var(--surface-fg))] truncate">{item.uploader_name || '-'}</p>
                          {item.uploader_email && (
                            <p className="text-xs text-[hsl(var(--surface-muted-fg))] truncate">{item.uploader_email}</p>
                          )}
                        </div>
                      </Link>
                    )}
                  </div>
                )}

                {/* Flagged banner */}
                {item.flagged && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-800 px-3 py-2 flex items-start gap-2">
                    <EyeOff className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <p className="text-sm">{t('flaggedHiddenUserBanner')}</p>
                  </div>
                )}
              </div>

              {/* Bottom Actions */}
              <div
                ref={footerSurfaceRef}
                className="flex gap-2 border-t bg-card px-4 py-3 text-[hsl(var(--surface-fg))]"
              >
                {showModeration && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onToggleFlag(item)}
                    className="text-xs flex-1"
                  >
                    <span>{item.flagged ? t('moderatedLabel') : t('moderateLabel')}</span>
                  </Button>
                )}
                {isDownloadEnabled && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadImage({ imageUrl: item.cdn_original || item.secure_url, albumSlug: albumSlug, fileName: `${albumSlug}-foto-ID_${item.id.substring(0, 5)}.jpg`, toast, userId: user.id });
                    }}
                    className="text-xs flex-1"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    <span>{t('download') || 'Descargar'}</span>
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onSingleDelete(item)}
                  className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50 flex-1"
                >
                  <span>{t('delete')}</span>
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
